import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { fetchActivities } from '../../services/referralService';
import { ScrollArea } from '../ui/ScrollArea';

// readOnly is currently unused — the component renders only display content. Any
// future "add comment" composer must gate its render on this prop.
const CommentThread = ({ refId, readOnly = false, reloadKey = 0, onActivitiesLoaded, includeInternalComments = false }) => { // eslint-disable-line no-unused-vars
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!refId) return undefined;
    // Guard against a late response stomping a newer refId/reloadKey.
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchActivities(refId, { includeInternalComments })
      .then(({ activities: data, currentWorkflowStep, visitedWorkflowSteps, latestStatus }) => {
        if (cancelled) return;
        setActivities(data);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        if (onActivitiesLoaded) {
          onActivitiesLoaded(data, currentWorkflowStep, visitedWorkflowSteps, latestStatus);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load comments.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refId, reloadKey, onActivitiesLoaded, includeInternalComments]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">No comments.</p>
      </div>
    );
  }

  return (
    <ScrollArea ref={scrollRef} className="flex-1 min-h-0 bg-card rounded-lg border border-border">
      <div className="space-y-2 px-3 pt-3 pb-3">
        {activities.map((activity, index) => {
          const isSL = activity.is_sl;
          const isOwn = activity.is_own;
          const isBlue = !isSL;
          // The backend redacts created_by_name to null for SL comments seen
          // by non-SL viewers — this check is defence-in-depth, not the
          // primary gate.
          const showName = !!activity.created_by_name;

          return (
            <div
              key={activity.id}
              data-testid="comment-bubble"
              data-own={isOwn ? 'true' : 'false'}
              className={cn(
                'flex flex-col gap-0.5 w-[80%] items-start',
                isOwn ? 'ml-auto' : 'mr-auto'
              )}
            >
              {/* Header */}
              <div className="flex items-center gap-1.5 justify-start flex-wrap">

                {/* Organization Name */}
                <span className="text-xs font-medium text-foreground">
                  {activity.party_name}
                </span>

                {/* User Name / Deleted User */}
                {activity.created_by_is_ghost ? (
                  showName ? (
                    <span className="text-xs text-muted-foreground">
                      ({activity.created_by_name} (deleted user))
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      (deleted user)
                    </span>
                  )
                ) : (
                  showName && (
                    <span className="text-xs text-muted-foreground">
                      ({activity.created_by_name})
                    </span>
                  )
                )}
                {/* Date */}
                <span className="text-[11px] text-muted-foreground">{activity.created_date}</span>
              </div>

              {/* Comment Bubble */}
              <div
                className={cn(
                  'w-full rounded-lg px-3 py-2 text-sm leading-relaxed text-left',
                  isBlue
                    ? 'bg-primary/5 text-foreground border border-primary/10'
                    : 'bg-muted/40 text-foreground border border-transparent',
                  isOwn ? 'rounded-tr-sm' : 'rounded-tl-sm'
                )}
              >
                {/* Status & Reason */}
                {index === 0 && activity.universal_reason_display && (
                  <div className="flex flex-col gap-1.5 mb-1.5">
                    <div className="flex flex-wrap items-center gap-x-4 text-xs">
                      <div>
                        <span className="font-semibold text-foreground">Status:</span>{' '}
                        <span className="text-muted-foreground">{activity.universal_reason_display}</span>
                      </div>
                      {activity.reason_display && (
                        <div>
                          <span className="font-semibold text-foreground">Reason:</span>{' '}
                          <span className="text-muted-foreground">{activity.reason_display}</span>
                        </div>
                      )}
                    </div>
                    <hr className="border-t border-border/50" />
                  </div>
                )}

                {/* Comment */}
                <div>{activity.comment}</div>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};

export { CommentThread };
